/**
 * Deal Models: the library, one model's current source, its catalogue,
 * fixtures, preview, per-model and per-user settings, plus the two read-only
 * discovery endpoints the composer's variables rail stands on and the
 * per-property overrides the Data area edits.
 *
 * Every deal-model call is lossless (`lossless: true`): money and revisions are
 * exact decimal tokens and never pass through a host Number. Every response is
 * parsed fail-closed by `dealModelContract.ts`, and every receipt must echo the
 * identity it was asked about (model, revision, fingerprint, fixture,
 * idempotency key) or the call fails as a contract mismatch.
 *
 * The list reuses `keys.dealModels` from `queries.ts`, so a create, clone or
 * save here refreshes the register's model picker too.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, data, type ApiCall } from './http'
import type { EndpointName } from './endpoints'
import { keys } from './queries'
import { withParsedLayouts, type DealModelLayout } from './dealModelLayout'
import { withModelFreshness } from './financial'
import {
  assertCanonicalUuid,
  assertJsonUtf8Ceiling,
  assertPositiveRevision,
  assertSettingsRevision,
  assertSourceDocumentResources,
  DEAL_MODEL_RESOURCE_LIMITS,
  parseCloneOutcome,
  parseDealModelCatalogue,
  parseDealModelCurrent,
  parseDealModelDetail,
  parseDealModelFixtures,
  parseDealModelList,
  parseDealModelPreview,
  parseDealModelSettings,
  parseDealModelUserSettings,
  parseFixtureMutation,
  parseFixtureRun,
  parseResetOutcome,
  validateExactRequestValues,
  validateFixtureDocument,
  validateUserSettingsRequestValues,
} from './dealModelContract'
import type {
  CreateDealModelInput,
  DealModelFixtureDocument,
  DealModelFixtureList,
  DealModelValueDescriptor,
  PropertyDealModelChoice,
} from './dealModelTypes'

export type * from './dealModelTypes'

/* ---------------------------------------------------------------- keys */

export const dealModelKeys = {
  list: keys.dealModels,
  model: (modelId: string) => ['dealModel', modelId] as const,
  current: (modelId: string) => ['dealModelCurrent', modelId] as const,
  catalogueOf: (modelId: string) => ['dealModelCatalogue', modelId] as const,
  catalogue: (modelId: string, revision: string, scopeEntityId: string | null) => ['dealModelCatalogue', modelId, revision, scopeEntityId] as const,
  fixtures: (modelId: string) => ['dealModelFixtures', modelId] as const,
  settings: (modelId: string) => ['dealModelSettings', modelId] as const,
  userSettings: ['dealModelUserSettings'] as const,
  calculationFields: ['calculationFields'] as const,
  calculationVariables: ['calculationVariableRegistry'] as const,
  /** Under the `dealsV2` prefix that refurbishment-preference saves already invalidate. */
  propertyModels: (propertyId: string) => ['dealsV2', 'propertyModels', propertyId] as const,
  propertyOverrides: (profileId: string, propertyId: string) => ['dealsV2', 'propertyOverrides', profileId, propertyId] as const,
  /** Mutation key of every property-override save for one property, so callers can exclude each other. */
  savePropertyOverrides: (propertyId: string) => ['saveDealsV2PropertyOverrides', propertyId] as const,
}

/* ------------------------------------------------------------- transport */

/** A deal-model call: lossless both ways, with the frozen request-size ceiling checked first. */
async function dealModelCall(name: EndpointName, call: ApiCall & { bodyUtf8Limit?: number } = {}): Promise<unknown> {
  const { bodyUtf8Limit = DEAL_MODEL_RESOURCE_LIMITS.evaluationInputUtf8Bytes, ...rest } = call
  if (rest.body !== undefined) assertJsonUtf8Ceiling(rest.body, `${name} request body`, bodyUtf8Limit)
  return api(name, { ...rest, lossless: true })
}

function assertIdentity<T>(actual: T, expected: T, field: string): T {
  if (actual !== expected) throw new ApiError(502, 'deal_model_contract_mismatch', `${field} did not echo the request identity`)
  return actual
}

/* --------------------------------------------------------------- library */

export function useDealModelList(enabled = true) {
  return useQuery({
    queryKey: dealModelKeys.list,
    queryFn: async ({ signal }) => parseDealModelList(await dealModelCall('dealModels', { signal })),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export function useCreateDealModel() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateDealModelInput) => {
      const logicalModelId = assertCanonicalUuid(input.logicalModelId, 'logicalModelId')
      const sourceDocument = assertSourceDocumentResources(input.sourceDocument)
      const parsed = parseDealModelDetail(await dealModelCall('createDealModel', {
        body: { ...input, logicalModelId, sourceDocument },
        bodyUtf8Limit: DEAL_MODEL_RESOURCE_LIMITS.sourceUtf8Bytes,
      }))
      assertIdentity(parsed.logicalModelId, logicalModelId, 'logicalModelId')
      return parsed
    },
    onSuccess: async (created) => {
      client.setQueryData(dealModelKeys.model(created.logicalModelId), created)
      await client.invalidateQueries({ queryKey: dealModelKeys.list })
    },
  })
}

export function useDealModel(modelId: string, enabled = true) {
  return useQuery({
    queryKey: dealModelKeys.model(modelId),
    queryFn: async ({ signal }) => {
      const parsed = parseDealModelDetail(await dealModelCall('dealModel', { params: { modelId }, signal }))
      assertIdentity(parsed.logicalModelId, modelId, 'logicalModelId')
      return parsed
    },
    enabled: enabled && modelId.length > 0,
    retry: false,
  })
}

export function useCloneDealModel() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ modelId, idempotencyKey }: { modelId: string; idempotencyKey: string }) => parseCloneOutcome(
      await dealModelCall('cloneDealModel', { params: { modelId }, body: {}, idempotencyKey }),
      { sourceModelId: modelId, idempotencyKey },
    ),
    onSuccess: async () => client.invalidateQueries({ queryKey: dealModelKeys.list }),
  })
}

/* ---------------------------------------------------------- current draft */

export function useDealModelCurrent(modelId: string, enabled = true) {
  return useQuery({
    queryKey: dealModelKeys.current(modelId),
    queryFn: async ({ signal }) => {
      const parsed = parseDealModelCurrent(await dealModelCall('dealModelCurrent', { params: { modelId }, signal }))
      assertIdentity(parsed.logicalModelId, modelId, 'current.logicalModelId')
      return parsed
    },
    enabled: enabled && modelId.length > 0,
    retry: false,
  })
}

export function usePatchDealModelCurrent(modelId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ expectedRevision, sourceDocument }: { expectedRevision: string; sourceDocument: unknown }) => {
      const revision = assertPositiveRevision(expectedRevision, 'expectedRevision')
      const exactSourceDocument = assertSourceDocumentResources(sourceDocument)
      const parsed = parseDealModelCurrent(await dealModelCall('patchDealModelCurrent', {
        params: { modelId },
        body: { expectedRevision: revision, sourceDocument: exactSourceDocument },
        bodyUtf8Limit: DEAL_MODEL_RESOURCE_LIMITS.sourceUtf8Bytes,
      }))
      assertIdentity(parsed.logicalModelId, modelId, 'current.logicalModelId')
      return parsed
    },
    onSuccess: async (updated) => {
      client.setQueryData(dealModelKeys.current(modelId), updated)
      await Promise.all([
        client.invalidateQueries({ queryKey: dealModelKeys.model(modelId) }),
        client.invalidateQueries({ queryKey: dealModelKeys.list }),
        client.invalidateQueries({ queryKey: dealModelKeys.catalogueOf(modelId), refetchType: 'none' }),
        // A new recipe makes saved results stale: the lot page and the register re-read them.
        client.invalidateQueries({ queryKey: ['dealsV2'] }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
      ])
    },
  })
}

/**
 * Reset a system built-in to the factory document the server embeds.
 *
 * Admin-only and gated by `capabilities.reset`; the caller mints the
 * idempotency key, which travels in the body (this route's contract) and in
 * the transport header, and the receipt echoes it back.
 */
export function useResetDealModel(modelId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ idempotencyKey }: { idempotencyKey: string }) => parseResetOutcome(
      await dealModelCall('resetDealModel', { params: { modelId }, body: { idempotencyKey: assertCanonicalUuid(idempotencyKey, 'idempotencyKey') }, idempotencyKey }),
      { logicalModelId: modelId, idempotencyKey },
    ),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: dealModelKeys.current(modelId) }),
        client.invalidateQueries({ queryKey: dealModelKeys.model(modelId) }),
        client.invalidateQueries({ queryKey: dealModelKeys.fixtures(modelId) }),
        client.invalidateQueries({ queryKey: dealModelKeys.catalogueOf(modelId), refetchType: 'none' }),
        client.invalidateQueries({ queryKey: dealModelKeys.list }),
        // The factory document is a new recipe: the lot page and the register re-read their saved results.
        client.invalidateQueries({ queryKey: ['dealsV2'] }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
      ])
    },
  })
}

export function useDealModelCatalogue(modelId: string, revision: string, scopeEntityId?: string) {
  return useQuery({
    queryKey: dealModelKeys.catalogue(modelId, revision, scopeEntityId ?? null),
    queryFn: async ({ signal }) => {
      const checkedRevision = assertPositiveRevision(revision, 'revision')
      const parsed = parseDealModelCatalogue(await dealModelCall('dealModelCatalogue', { params: { modelId }, query: { revision: checkedRevision, scopeEntityId }, signal }))
      if (parsed.modelId !== modelId || parsed.revision !== checkedRevision) throw new Error('Catalogue identity does not match the selected draft')
      return parsed
    },
    enabled: modelId.length > 0 && revision.length > 0,
    retry: false,
  })
}

export function usePreviewDealModel(modelId: string) {
  return useMutation({
    mutationFn: async ({ revision, sourceDocument, requestFingerprint, propertyId, scenarioId, traceLevel = 'summary' }: {
      revision: string
      sourceDocument: unknown
      requestFingerprint: string
      propertyId?: string
      scenarioId?: string
      traceLevel?: 'none' | 'summary' | 'full'
    }) => {
      const checkedRevision = assertPositiveRevision(revision, 'revision')
      const exactSourceDocument = assertSourceDocumentResources(sourceDocument)
      const parsed = parseDealModelPreview(await dealModelCall('previewDealModel', {
        params: { modelId },
        body: { revision: checkedRevision, sourceDocument: exactSourceDocument, requestFingerprint, propertyId, scenarioId, traceLevel },
        bodyUtf8Limit: DEAL_MODEL_RESOURCE_LIMITS.sourceUtf8Bytes,
      }))
      assertIdentity(parsed.logicalModelId, modelId, 'preview.logicalModelId')
      assertIdentity(parsed.revision, checkedRevision, 'preview.revision')
      assertIdentity(parsed.requestFingerprint, requestFingerprint, 'preview.requestFingerprint')
      return parsed
    },
  })
}

/* --------------------------------------------------------------- fixtures */

export function useDealModelFixtures(modelId: string) {
  return useQuery({
    queryKey: dealModelKeys.fixtures(modelId),
    queryFn: async ({ signal }) => {
      const parsed = parseDealModelFixtures(await dealModelCall('dealModelFixtures', { params: { modelId }, signal }))
      assertIdentity(parsed.logicalModelId, modelId, 'fixtures.logicalModelId')
      return parsed
    },
    enabled: modelId.length > 0,
    retry: false,
  })
}

export function useCreateDealModelFixture(modelId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { expectedRevision: string; fixture: DealModelFixtureDocument }) => {
      const revision = assertPositiveRevision(input.expectedRevision, 'expectedRevision')
      const parsed = parseFixtureMutation(await dealModelCall('createDealModelFixture', {
        params: { modelId },
        body: { expectedRevision: revision, fixture: validateFixtureDocument(input.fixture) },
        bodyUtf8Limit: DEAL_MODEL_RESOURCE_LIMITS.sourceUtf8Bytes,
      }), 'upsert')
      assertIdentity(parsed.logicalModelId, modelId, 'fixture.logicalModelId')
      return parsed
    },
    onSuccess: async () => client.invalidateQueries({ queryKey: dealModelKeys.fixtures(modelId) }),
  })
}

export function usePatchDealModelFixture(modelId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ fixtureId, expectedRevision, fixture }: { fixtureId: string; expectedRevision: string; fixture: DealModelFixtureDocument }) => {
      const revision = assertPositiveRevision(expectedRevision, 'expectedRevision')
      const validated = validateFixtureDocument(fixture)
      assertIdentity(validated.id, fixtureId, 'fixture.id')
      const parsed = parseFixtureMutation(await dealModelCall('patchDealModelFixture', {
        params: { modelId, fixtureId },
        body: { expectedRevision: revision, fixture: validated },
        bodyUtf8Limit: DEAL_MODEL_RESOURCE_LIMITS.sourceUtf8Bytes,
      }), 'upsert')
      assertIdentity(parsed.logicalModelId, modelId, 'fixture.logicalModelId')
      assertIdentity(parsed.fixture.fixtureId, fixtureId, 'fixture.fixtureId')
      return parsed
    },
    onSuccess: async () => client.invalidateQueries({ queryKey: dealModelKeys.fixtures(modelId) }),
  })
}

export function useDeleteDealModelFixture(modelId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ fixtureId, expectedRevision }: { fixtureId: string; expectedRevision: string }) => {
      const revision = assertPositiveRevision(expectedRevision, 'expectedRevision')
      const parsed = parseFixtureMutation(await dealModelCall('deleteDealModelFixture', { params: { modelId, fixtureId }, body: { expectedRevision: revision } }), 'delete')
      assertIdentity(parsed.logicalModelId, modelId, 'fixture.logicalModelId')
      return parsed
    },
    onSuccess: async (updated, variables) => {
      client.setQueryData<DealModelFixtureList>(dealModelKeys.fixtures(modelId), current => current
        ? { ...current, revision: updated.revision, sourceSha256: updated.sourceSha256, capabilities: updated.capabilities, items: current.items.filter(item => item.fixtureId !== variables.fixtureId) }
        : current)
      await client.invalidateQueries({ queryKey: dealModelKeys.fixtures(modelId) })
    },
  })
}

export function useRunDealModelFixtures(modelId: string) {
  return useMutation({
    mutationFn: async ({ fixtureId, revision, requestFingerprint }: { fixtureId?: string; revision: string; requestFingerprint: string }) => {
      const checkedRevision = assertPositiveRevision(revision, 'revision')
      const body = { revision: checkedRevision, requestFingerprint, traceLevel: 'summary', watchedOutputs: [] }
      const parsed = parseFixtureRun(fixtureId
        ? await dealModelCall('runDealModelFixture', { params: { modelId, fixtureId }, body })
        : await dealModelCall('runDealModelFixtures', { params: { modelId }, body }))
      assertIdentity(parsed.logicalModelId, modelId, 'fixture run.logicalModelId')
      assertIdentity(parsed.revision, checkedRevision, 'fixture run.revision')
      assertIdentity(parsed.requestFingerprint, requestFingerprint, 'fixture run.requestFingerprint')
      assertIdentity(parsed.fixtureId, fixtureId ?? null, 'fixture run.fixtureId')
      return parsed
    },
  })
}

/* --------------------------------------------------------------- settings */

export function useDealModelUserSettings(enabled = true) {
  return useQuery({
    queryKey: dealModelKeys.userSettings,
    queryFn: async ({ signal }) => parseDealModelUserSettings(await dealModelCall('dealModelUserSettings', { signal })),
    enabled,
    retry: false,
  })
}

export function usePutDealModelUserSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ expectedRevision, defaultAssumptions, descriptors }: { expectedRevision: string; defaultAssumptions: Record<string, string | boolean | null>; descriptors: DealModelValueDescriptor[] }) => parseDealModelUserSettings(
      await dealModelCall('putDealModelUserSettings', { body: { expectedRevision: assertSettingsRevision(expectedRevision, 'expectedRevision'), defaultAssumptions: validateUserSettingsRequestValues(defaultAssumptions, descriptors, 'defaultAssumptions') } }),
    ),
    onSuccess: async (updated) => {
      client.setQueryData(dealModelKeys.userSettings, updated)
      await Promise.all([
        client.invalidateQueries({ queryKey: ['dealsV2'] }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
      ])
    },
  })
}

export function useDealModelSettings(modelId: string, enabled = true) {
  return useQuery({
    queryKey: dealModelKeys.settings(modelId),
    queryFn: async ({ signal }) => {
      const parsed = parseDealModelSettings(await dealModelCall('dealModelSettings', { params: { modelId }, signal }))
      assertIdentity(parsed.logicalModelId, modelId, 'model settings.logicalModelId')
      return parsed
    },
    enabled: enabled && modelId.length > 0,
    retry: false,
  })
}

export function usePutDealModelSettings(modelId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ expectedRevision, values, descriptors }: { expectedRevision: string; values: Record<string, string | boolean | null>; descriptors: DealModelValueDescriptor[] }) => {
      const parsed = parseDealModelSettings(await dealModelCall('putDealModelSettings', {
        params: { modelId },
        body: { expectedRevision: assertSettingsRevision(expectedRevision, 'expectedRevision'), values: validateExactRequestValues(values, descriptors, 'modelSettings') },
      }))
      assertIdentity(parsed.logicalModelId, modelId, 'model settings.logicalModelId')
      return parsed
    },
    onSuccess: async (updated) => {
      client.setQueryData(dealModelKeys.settings(modelId), updated)
      // New model settings make saved results stale: the lot page and the register re-read them.
      await Promise.all([
        client.invalidateQueries({ queryKey: ['dealsV2'] }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
      ])
    },
  })
}

/* --------------------------------------- composer discovery (read-only) */

export interface CalculationFieldDefinition {
  catalogueVersion: string
  fieldKey: string
  bindingPath: string | null
  resultPointer: string | null
  bindingRole: string
  groupKey: string
  displayOrder: number
  label: string
  description: string
  dataType: string
  unit: string | null
  nullable: boolean
  missingSemantics: string
  sourceCategory: string
  authoritativeStatus: string
  normalSourceFlow: string
  refreshCadence: string
  derivation: string | null
  dependencyFieldKeys: string[]
  consumerRuleIds: string[]
  filterEligible: boolean
  sortEligible: boolean
  rankEligible: boolean
  sensitive: boolean
}

export interface CalculationModelIdentity { modelKey: string; modelId: string; modelVersion: string; modelHash: string; activationRevision: number }

export interface CalculationFieldCatalogue {
  contractVersion: 'property-card-v8'
  catalogueVersion: 'property-card-v8'
  sourceRegistry: string
  model: CalculationModelIdentity
  fields: CalculationFieldDefinition[]
}

type JsonRecord = Record<string, unknown>
function calculationContractError(message: string): never { throw new ApiError(502, 'calculation_contract_mismatch', message) }
function jsonRecord(value: unknown, name: string): JsonRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) calculationContractError(`${name} must be an object`)
  return value as JsonRecord
}
function str(value: unknown, name: string): string { if (typeof value !== 'string') calculationContractError(`${name} must be a string`); return value }
function nullableStr(value: unknown, name: string): string | null { return value === null || value === undefined ? null : str(value, name) }
function num(value: unknown, name: string): number { if (typeof value !== 'number' || !Number.isFinite(value)) calculationContractError(`${name} must be a finite number`); return value }
function bool(value: unknown, name: string): boolean { if (typeof value !== 'boolean') calculationContractError(`${name} must be a boolean`); return value }
function strings(value: unknown, name: string): string[] {
  if (!Array.isArray(value) || value.some(item => typeof item !== 'string')) calculationContractError(`${name} must be a string array`)
  return value as string[]
}

function parseCalculationField(value: unknown, index: number): CalculationFieldDefinition {
  const field = jsonRecord(value, `fields[${index}]`)
  const name = `fields[${index}]`
  return {
    catalogueVersion: str(field.catalogueVersion, `${name}.catalogueVersion`), fieldKey: str(field.fieldKey, `${name}.fieldKey`), groupKey: str(field.groupKey, `${name}.groupKey`), displayOrder: num(field.displayOrder, `${name}.displayOrder`),
    bindingPath: nullableStr(field.bindingPath, `${name}.bindingPath`), resultPointer: nullableStr(field.resultPointer, `${name}.resultPointer`), bindingRole: str(field.bindingRole, `${name}.bindingRole`),
    label: str(field.label, `${name}.label`), description: str(field.description, `${name}.description`), dataType: str(field.dataType, `${name}.dataType`), unit: nullableStr(field.unit, `${name}.unit`),
    nullable: bool(field.nullable, `${name}.nullable`), missingSemantics: str(field.missingSemantics, `${name}.missingSemantics`), sourceCategory: str(field.sourceCategory, `${name}.sourceCategory`), authoritativeStatus: str(field.authoritativeStatus, `${name}.authoritativeStatus`),
    normalSourceFlow: str(field.normalSourceFlow, `${name}.normalSourceFlow`), refreshCadence: str(field.refreshCadence, `${name}.refreshCadence`), derivation: nullableStr(field.derivation, `${name}.derivation`), dependencyFieldKeys: strings(field.dependencyFieldKeys, `${name}.dependencyFieldKeys`),
    consumerRuleIds: strings(field.consumerRuleIds, `${name}.consumerRuleIds`), filterEligible: bool(field.filterEligible, `${name}.filterEligible`), sortEligible: bool(field.sortEligible, `${name}.sortEligible`), rankEligible: bool(field.rankEligible, `${name}.rankEligible`), sensitive: bool(field.sensitive, `${name}.sensitive`),
  }
}

export function parseCalculationFieldCatalogue(value: unknown): CalculationFieldCatalogue {
  const body = jsonRecord(jsonRecord(value, 'calculation fields envelope').data, 'calculation fields data')
  if (body.contractVersion !== 'property-card-v8') calculationContractError('calculation fields contract must be property-card-v8')
  if (body.catalogueVersion !== 'property-card-v8' || !Array.isArray(body.fields)) calculationContractError('Unsupported field catalogue')
  const model = jsonRecord(body.model, 'catalogue model')
  return {
    contractVersion: 'property-card-v8', catalogueVersion: 'property-card-v8', sourceRegistry: str(body.sourceRegistry, 'sourceRegistry'),
    model: { modelKey: str(model.modelKey, 'model.modelKey'), modelId: str(model.modelId, 'model.modelId'), modelVersion: str(model.modelVersion, 'model.modelVersion'), modelHash: str(model.modelHash, 'model.modelHash'), activationRevision: num(model.activationRevision, 'model.activationRevision') },
    fields: body.fields.map(parseCalculationField),
  }
}

/** The calculation field catalogue (`property` / `listing` / `deal` / `market` / `area`): the variables rail's spine. */
export function useCalculationFieldCatalogue() {
  return useQuery({ queryKey: dealModelKeys.calculationFields, queryFn: async ({ signal }) => parseCalculationFieldCatalogue(await api('calculationFields', { signal })), retry: false })
}

export interface CalculationVariable {
  fieldKey: string
  namespace: string
  integration: string
  label: string
  description: string
  dataType: string
  unit: string | null
  nullable: boolean
  origin: 'computed' | 'raw'
  bindable: boolean
  bindingPath: string | null
  resultPointer: string | null
  sourcePointer: string | null
  missingSemantics: string
}

export interface CalculationVariables {
  contractVersion: 'calculation-variables-v1'
  registryHash: string
  sourceRegistryVersion: string
  variables: CalculationVariable[]
  namespaces: string[]
  integrations: string[]
  dataTypes: string[]
  origins: Array<'computed' | 'raw'>
}

/** The unified computed/raw variable registry; its metadata decides what can be bound. */
export function useCalculationVariables() {
  return useQuery({
    queryKey: dealModelKeys.calculationVariables,
    queryFn: async ({ signal }) => {
      const variables = data<CalculationVariables>(await api('calculationsVariables', { signal }))
      if (variables?.contractVersion !== 'calculation-variables-v1' || !Array.isArray(variables.variables)
        || variables.variables.some(field => typeof field.fieldKey !== 'string' || typeof field.bindable !== 'boolean'
          || !['computed', 'raw'].includes(field.origin) || (field.origin === 'raw' && field.bindable))) {
        throw new ApiError(502, 'calculation_variables_contract_mismatch', 'Variable discovery returned an invalid binding contract')
      }
      return variables
    },
    staleTime: 60_000,
  })
}

/* ------------------------------------------- Data area: property overrides */

export interface DealsV2PropertyModelChoice extends Pick<PropertyDealModelChoice, 'logicalModelId' | 'modelName' | 'ownership' | 'presentation' | 'diagnostics'> {
  availabilityState: PropertyDealModelChoice['availabilityState'] | 'updating'
  profileId: string | null
  /** Display layout (`deal-model-layout-v1`); absent on an older server or when its shape is not understood. */
  layout?: DealModelLayout
  /** Parsed as in financial.ts: `updating` when the latest saved result is stale, `null` when there is none. */
  freshness?: 'current' | 'updating' | null
  staleReasons?: string[]
}

export interface DealsV2PropertyModels {
  contractVersion: 'deals-v2-property-models-v1'
  propertyId: string
  defaultLogicalModelId: string | null
  models: DealsV2PropertyModelChoice[]
}

export interface DealsV2PropertyOverrides {
  profileId: string
  propertyId: string
  revision: string
  sourceSha256: string
  storedSourceSha256: string | null
  values: Record<string, unknown>
  jobId?: string | null
}

function isFinancialSyncBusy(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503 && error.code === 'FINANCIAL_SYNC_BUSY'
}

/** The saved Financial profiles for one property; finds the profile that carries this model. */
export function useDealsV2PropertyModels(propertyId: string, enabled = true) {
  return useQuery({
    queryKey: dealModelKeys.propertyModels(propertyId),
    queryFn: async ({ signal }) => {
      const models = data<DealsV2PropertyModels>(await api('dealsV2PropertyModels', { params: { propertyId }, signal }))
      if (models.propertyId !== propertyId || models.contractVersion !== 'deals-v2-property-models-v1') {
        throw new ApiError(502, 'deals_v2_contract_mismatch', 'Financial model list did not match this property')
      }
      // Shares its cache entry with financial.ts; both parse the optional layout and freshness the same way.
      return { ...models, models: Array.isArray(models.models) ? withModelFreshness(withParsedLayouts(models.models)) : models.models }
    },
    enabled: enabled && Boolean(propertyId),
    retry: (failureCount, error) => failureCount < 3 && isFinancialSyncBusy(error),
    retryDelay: attempt => Math.min(500 * 2 ** attempt, 2_000),
    refetchOnWindowFocus: false,
  })
}

/** The property-overrides read, shared by the hook and an on-demand `fetchQuery`. */
export function dealsV2PropertyOverridesQuery(profileId: string, propertyId: string) {
  return {
    queryKey: dealModelKeys.propertyOverrides(profileId, propertyId),
    queryFn: async ({ signal }: { signal: AbortSignal }) => data<DealsV2PropertyOverrides>(await api('dealModelPropertyOverrides', { params: { profileId, propertyId }, signal, lossless: true })),
    retry: false,
  } as const
}

export function useDealsV2PropertyOverrides(profileId: string, propertyId: string, enabled = true) {
  return useQuery({
    ...dealsV2PropertyOverridesQuery(profileId, propertyId),
    enabled: enabled && Boolean(profileId) && Boolean(propertyId),
  })
}

/**
 * Save one property's overrides. `propertyId` scopes the mutation key so the
 * lot page's callers can exclude each other (`useIsMutating`). A 409 means the
 * profile, recipe or revision moved: the model list (profile id and layout),
 * the result and the overrides are all re-read.
 */
export function useSaveDealsV2PropertyOverrides(propertyId?: string) {
  const client = useQueryClient()
  return useMutation({
    ...(propertyId ? { mutationKey: dealModelKeys.savePropertyOverrides(propertyId) } : {}),
    mutationFn: async ({ profileId, propertyId, expectedRevision, expectedSourceSha256, values }: { profileId: string; propertyId: string; expectedRevision: string; expectedSourceSha256: string; values: Record<string, unknown> }) =>
      data<DealsV2PropertyOverrides>(await api('dealsV2ProfileOverrides', { params: { profileId, propertyId }, body: { expectedRevision, expectedSourceSha256, values }, lossless: true })),
    onSuccess: async (saved) => {
      // The response is the stored overrides of the (possibly forked) profile: seed its read so the
      // rail keeps its values while the refreshed model list moves to that profile.
      if (!client.getQueryData(dealModelKeys.propertyOverrides(saved.profileId, saved.propertyId))) client.setQueryData(dealModelKeys.propertyOverrides(saved.profileId, saved.propertyId), saved)
      await Promise.all([
        client.invalidateQueries({ queryKey: dealModelKeys.propertyOverrides(saved.profileId, saved.propertyId) }),
        // A shared baseline is forked before storing a personal input override;
        // refresh the selected model's profile identity as well as its results.
        client.invalidateQueries({ queryKey: dealModelKeys.propertyModels(saved.propertyId) }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
        client.invalidateQueries({ queryKey: ['dealsV2'] }),
      ])
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) await client.invalidateQueries({ queryKey: ['dealsV2'] })
    },
  })
}
